<script setup lang="ts">
import {
	computed, reactive, ref,
} from "vue";

import { z } from "zod";

import { client } from "@/api";

import BrandIcon from "./BrandIcon.vue";
import BrokerSelect from "./BrokerSelect.vue";
import ClassASvg from "./ClassASvg.vue";

export type OPASlug = "cjp" | "tbmopa" | "ejoa" | "popa";

type BenefitIcon = "handshake" | "approach" | "flying" | "windsock";

type OPAConfig = {
	benefits: Array<{
		icon: BenefitIcon;
		title: string;
	}>;
	benefitsTitle: string;
	event: string;
	heading: {
		emphasis?: string;
		prefix: string;
		suffix?: string;
	};
	headingClass: string;
	logoAlt: string;
	logoClass: string;
	logoSrc: string;
	sideImageUrl: string;
	submitSuccessMessage: string;
};

const OPA_CONFIG: Record<OPASlug, OPAConfig> = {
	cjp: {
		benefits: [
			{
				icon: "handshake",
				title: "Enhanced insurance coverage for CJP members",
			},
			{
				icon: "approach",
				title: "Access to Virtual Flight Department™",
			},
			{
				icon: "flying",
				title: "Tailored Training Options, including In-Aircraft Training",
			},
			{
				icon: "windsock",
				title: "CJP-FOQA™ program bursary",
			},
		],
		benefitsTitle: "CJP Exclusive Benefits",
		event: "cjp-landing-page",
		heading: {
			prefix: "Elevating your insurance experience with a shared commitment to safety and continuous improvement",
		},
		headingClass: "text-2xl lg:text-3xl mb-4 font-serif text-center max-w-3xl mx-auto",
		logoAlt: "CJP Logo",
		logoClass: "w-80",
		logoSrc: "https://r2public.classa.ai/cjp_logo_dark.png",
		sideImageUrl: "https://r2public.classa.ai/cessna1.jpg",
		submitSuccessMessage: "Thank you for your interest! A Class A representative will contact you soon to discuss your exclusive CJP benefits.",
	},
	tbmopa: {
		benefits: [
			{
				icon: "approach",
				title: "Access to Virtual Flight Department™",
			},
			{
				icon: "windsock",
				title: "Safety Enhancements & Skills Training Reimbursement for TAAP, Air-Sync, and Approved Enhancement Training",
			},
			{
				icon: "handshake",
				title: "No Claims Bonus Premium Return TBMOPA Members of 5%, 10% for TAAP Standard",
			},
		],
		benefitsTitle: "TBMOPA Member Benefits",
		event: "tbmopa-landing-page",
		heading: {
			emphasis: "Pilots who Think Ahead",
			prefix: "Aviation Insurance built for ",
		},
		headingClass: "text-2xl lg:text-4xl mb-4 font-serif text-center max-w-xl mx-auto",
		logoAlt: "TBMOPA Logo",
		logoClass: "w-50",
		logoSrc: "https://r2public.classa.ai/opas/landing-pages/tbmopa-logo.png",
		sideImageUrl: "https://r2public.classa.ai/opas/landing-pages/tbm.jpg",
		submitSuccessMessage: "Thank you for your interest! A Class A representative will contact you soon to discuss your exclusive TBMOPA benefits.",
	},
	ejoa: {
		benefits: [],
		benefitsTitle: "EJOA Member Benefits",
		event: "ejoa-landing-page",
		heading: {
			prefix: "A Safer Sky awaits",
		},
		headingClass: "text-2xl lg:text-4xl mb-4 font-serif text-center max-w-2xl mx-auto",
		logoAlt: "EJOA Logo",
		logoClass: "w-50",
		logoSrc: "https://r2public.classa.ai/opas/landing-pages/ejoa.webp",
		sideImageUrl: "https://r2public.classa.ai/opas/landing-pages/phenom.jpg",
		submitSuccessMessage: "Thank you for your interest! We'll be in touch soon.",
	},
	popa: {
		benefits: [],
		benefitsTitle: "A shared dedication to safety",
		event: "popa-landing-page",
		heading: {
			prefix: "A Shared Dedication to Safety",
		},
		headingClass: "text-2xl lg:text-4xl mb-4 font-serif text-center max-w-2xl mx-auto",
		logoAlt: "POPA Logo",
		logoClass: "w-50",
		logoSrc: "https://r2public.classa.ai/opas/landing-pages/popa-logo.png",
		sideImageUrl: "https://r2public.classa.ai/opas/landing-pages/pc-12.webp",
		submitSuccessMessage: "Thank you for your interest! We'll be in touch soon.",
	},
};

const props = defineProps<{
	opa: OPASlug;
}>();

const config = computed(() => OPA_CONFIG[props.opa]);

const formSchema = z.object({
	email: z.email("Please enter a valid email address"),
	name: z.string().min(2, "Name must be at least 2 characters"),
	phone: z.string().nullable().default(null),
	tailNumber: z.string().min(1, "Tail number is required"),
	broker: z.string().nullable().default(null),
});

type FormData = z.output<typeof formSchema>;

const formState = reactive<Partial<FormData>>({
	email: "",
	name: "",
	phone: "",
	tailNumber: "",
	broker: "",
});

const toast = useToast();

const isSubmitting = ref(false);
const submitMessage = ref<{ type: "success" | "error"; message: string } | null>(null);
const label = computed(() => {
	return isSubmitting.value ? "Submitting..." : "Unlock Your Benefits";
});

const resetForm = () => {
	formState.email = "";
	formState.name = "";
	formState.phone = "";
	formState.tailNumber = "";
	formState.broker = "";
};

const onSubmit = async (_event: unknown) => {
	try {
		isSubmitting.value = true;
		submitMessage.value = null;

		const result = await client.api.v1.events.signup.post({
			email: formState.email!,
			name: formState.name!,
			phone: formState.phone || null,
			tailNumber: formState.tailNumber!,
			broker: formState.broker || null,
			event: config.value.event,
		});

		if (result.data) {
			submitMessage.value = {
				type: "success",
				message: config.value.submitSuccessMessage,
			};
			resetForm();
		} else {
			toast.add({
				title: "Oh no! Something went wrong",
				description: "Please try again or contact us directly for assistance.",
				color: "error",
			});
		}
	} catch (error) {
		console.error("Submission error:", error);
		submitMessage.value = {
			type: "error",
			message: "Submission failed. Please try again or contact us directly for assistance.",
		};
	} finally {
		isSubmitting.value = false;
	}
};
</script>

<template>
  <div class="min-h-screen">
    <section class="bg-gray-100 min-h-[50vh] py-12">
      <div class="max-w-5xl mx-auto">
        <div class="w-full grid lg:grid-cols-2 gap-4 items-center mb-12 max-w-5xl mx-auto">
          <div class="flex items-center justify-center">
            <ClassASvg class="w-60" />
          </div>
          <div class="flex items-center justify-center">
            <img
              :src="config.logoSrc"
              :alt="config.logoAlt"
              :class="config.logoClass"
            >
          </div>
        </div>
        <h3 :class="config.headingClass">
          {{ config.heading.prefix }}
          <span
            v-if="config.heading.emphasis"
            class="italic"
          >
            {{ config.heading.emphasis }}
          </span>
          {{ config.heading.suffix }}
        </h3>
        <div class="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 bg-gray-900 p-8 mt-8">
          <h2 class="text-2xl lg:text-4xl font-serif mb-8 text-white">
            Sign Up
          </h2>
          <UForm
            :schema="formSchema"
            :state="formState"
            class="space-y-6"
            @submit="onSubmit"
          >
            <div class="grid md:grid-cols-2 gap-6">
              <UFormField
                label="Full Name"
                name="name"
                required
                :ui="{
                  label: 'text-white',
                }"
              >
                <UInput
                  v-model="formState.name"
                  placeholder="Enter your full name"
                  size="lg"
                  class="w-full"
                />
              </UFormField>

              <UFormField
                label="Email Address"
                name="email"
                required
                :ui="{
                  label: 'text-white',
                }"
              >
                <UInput
                  v-model="formState.email"
                  type="email"
                  placeholder="your.email@example.com"
                  size="lg"
                  class="w-full"
                />
              </UFormField>
            </div>

            <div class="grid md:grid-cols-2 gap-6">
              <UFormField
                label="Phone Number"
                name="phone"
                :ui="{
                  label: 'text-white',
                }"
              >
                <UInput
                  v-model="formState.phone"
                  placeholder="(555) 123-4567"
                  size="lg"
                  class="w-full"
                />
              </UFormField>

              <UFormField
                label="Tail Number"
                name="tailNumber"
                required
                :ui="{
                  label: 'text-white',
                }"
              >
                <UInput
                  v-model="formState.tailNumber"
                  placeholder="N####"
                  size="lg"
                  class="w-full"
                />
              </UFormField>
            </div>

            <UFormField
              label="Brokerage"
              :ui="{
                label: 'text-white',
              }"
            >
              <BrokerSelect
                v-model="formState.broker"
                class="w-full"
              />
            </UFormField>

            <UButton
              type="submit"
              color="primary"
              size="xl"
              :loading="isSubmitting"
              :disabled="isSubmitting"
              :label="label"
              icon="ic-round-airplane-ticket"
              class="mt-4"
            />
          </UForm>

          <div
            v-if="submitMessage"
            class="mt-6 p-4 rounded-lg"
            :class="submitMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'"
          >
            <p class="text-center font-medium">
              {{ submitMessage.message }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section
      class="bg-gray-900/90 text-white grid min-h-[25vh] gap-4"
      :class="config.benefits.length > 0 ? 'grid-cols-3' : 'grid-cols-2'"
    >
      <div class="hidden lg:flex items-center justify-center overflow-hidden">
        <img
          src="https://r2public.classa.ai/app-screen.png"
          alt="Virtual Flight Department App"
          class="w-72 mt-12"
        >
      </div>
      <div
        v-if="config.benefits.length > 0"
        class="col-span-3 lg:col-span-1 flex items-center justify-center"
      >
        <div>
          <h3 class="text-2xl lg:text-3xl mb-4 font-serif max-w-3xl mx-auto underline decoration-primary-500">
            {{ config.benefitsTitle }}
          </h3>
          <ul class="space-y-6">
            <li
              v-for="benefit in config.benefits"
              :key="benefit.title"
              class="flex items-center gap-2"
            >
              <BrandIcon
                :icon="benefit.icon"
                class="size-10 lg:size-14 shrink-0 text-white"
              />
              <span class="lg:text-lg">
                {{ benefit.title }}
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div
        class="hidden lg:block col-span-1"
        :style="{
          backgroundImage: `url(${config.sideImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }"
      />
    </section>
    <div class="grow bg-primary-500 max-h-[500px] lg:h-[30vh] h-60">
      <UContainer class="flex justify-center items-end size-full mx-auto">
        <ClassASvg />
      </UContainer>
    </div>
  </div>
</template>
